import { useQuery } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import { Users, UserCheck, Car, TrendingUp, DollarSign, MapPin, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { cn } from '../utils/cn';

const StatCard = ({ title, value, icon: Icon, trend, color = 'primary', subtitle }) => {
  const colorConfigs = {
    primary: {
      bg: 'bg-gradient-to-br from-primary/10 to-primary/5',
      iconBg: 'bg-gradient-to-br from-primary to-primary-dark',
      text: 'text-primary',
    },
    success: {
      bg: 'bg-gradient-to-br from-success/10 to-success/5',
      iconBg: 'bg-gradient-to-br from-success to-success-dark',
      text: 'text-success',
    },
    warning: {
      bg: 'bg-gradient-to-br from-warning/10 to-warning/5',
      iconBg: 'bg-gradient-to-br from-warning to-warning-dark',
      text: 'text-warning',
    },
    info: {
      bg: 'bg-gradient-to-br from-info/10 to-info/5',
      iconBg: 'bg-gradient-to-br from-info to-info-dark',
      text: 'text-info',
    },
  };

  const config = colorConfigs[color] || colorConfigs.primary;
  const isPositive = trend !== undefined && trend !== null && trend > 0;

  return (
    <div className="group relative bg-surface dark:bg-surface-dark rounded-2xl p-6 border border-border dark:border-border-dark shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Gradient Background */}
      <div className={cn('absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300', config.bg)} />
      
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className={cn('p-3 rounded-xl shadow-lg', config.iconBg)}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          {trend !== undefined && (
            <div className={cn(
              'flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold',
              isPositive ? 'bg-success-light dark:bg-success-dark text-success' : 'bg-danger-light dark:bg-danger-dark text-danger'
            )}>
              {isPositive ? (
                <ArrowUpRight className="w-3 h-3" />
              ) : (
                <ArrowDownRight className="w-3 h-3" />
              )}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div>
          <h3 className="text-3xl font-bold text-text dark:text-text-dark mb-1">{value}</h3>
          <p className="text-sm font-medium text-text-secondary dark:text-text-secondary-dark">{title}</p>
          {subtitle && (
            <p className="text-xs text-text-tertiary dark:text-text-tertiary-dark mt-1">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['fleet-dashboard'],
    queryFn: async () => {
      try {
        const response = await fleetService.getDashboard();
        // API returns: { success: true, data: { kpis: {...}, fleet: {...}, recentActivity: [] } }
        // axios response.data = { success: true, data: {...} }
        // So we need response.data.data to get the actual data object
        const data = response.data?.data || response.data;
        console.log('Dashboard Data:', data);
        console.log('KPIs:', data?.kpis);
        return data;
      } catch (err) {
        console.error('Dashboard API error:', err);
        throw err;
      }
    },
    retry: 1,
    retryDelay: 1000,
  });

  const { data: analyticsData } = useQuery({
    queryKey: ['fleet-analytics'],
    queryFn: async () => {
      try {
        const response = await fleetService.getAnalytics();
        return response.data;
      } catch (err) {
        console.error('Analytics API error:', err);
        return null;
      }
    },
    retry: 1,
    retryDelay: 1000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <div className="text-text-secondary dark:text-text-secondary-dark">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to load dashboard data';
    const statusCode = error.response?.status;
    
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-text-dark mb-2">Dashboard</h1>
        </div>
        <div className="bg-danger-light dark:bg-danger-dark border border-danger rounded-2xl p-6 shadow-lg">
          <div className="text-center">
            <p className="text-danger font-semibold mb-2">Error loading dashboard data</p>
            <p className="text-text-secondary dark:text-text-secondary-dark text-sm mb-2">
              {errorMessage}
            </p>
            {statusCode === 401 && (
              <p className="text-text-secondary dark:text-text-secondary-dark text-xs">
                Your session may have expired. Please try logging in again.
              </p>
            )}
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors shadow-lg"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Extract KPIs from dashboard data
  // API response structure: { success: true, data: { kpis: {...}, fleet: {...}, recentActivity: [] } }
  // After queryFn processing: dashboardData = { kpis: {...}, fleet: {...}, recentActivity: [] }
  const kpis = dashboardData?.kpis || {};
  
  // Debug: Log to verify data structure
  if (process.env.NODE_ENV === 'development') {
    console.log('Dashboard Data:', dashboardData);
    console.log('KPIs:', kpis);
  }
  
  // Calculate trends if we have previous period data (optional - can be enhanced later)
  // For now, we'll show stats without trends or make trends optional
  const stats = [
    {
      title: 'Total Drivers',
      value: kpis.totalDrivers ?? 0,
      icon: Users,
      color: 'primary',
      subtitle: 'All registered drivers',
    },
    {
      title: 'Active Drivers',
      value: kpis.activeDrivers ?? 0,
      icon: UserCheck,
      color: 'success',
      subtitle: 'Currently online',
    },
    {
      title: 'Total Vehicles',
      value: kpis.totalVehicles ?? 0,
      icon: Car,
      color: 'info',
      subtitle: 'Fleet vehicles',
    },
    {
      title: 'Active Rides',
      value: kpis.activeRides ?? 0,
      icon: MapPin,
      color: 'warning',
      subtitle: 'In progress',
    },
    {
      title: 'Today\'s Rides',
      value: kpis.todayRides ?? 0,
      icon: TrendingUp,
      color: 'success',
      subtitle: 'Completed today',
    },
    {
      title: 'Today\'s Revenue',
      value: `NOK ${(kpis.todayRevenue ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: 'warning',
      subtitle: 'Total earnings',
    },
  ];

  // Prepare chart data from analytics
  const revenueData = analyticsData?.revenue?.trends || analyticsData?.revenue?.byDriver || [];
  const ridesData = analyticsData?.rides?.trends || [];

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl lg:text-4xl font-bold text-text dark:text-text-dark mb-2">
          Dashboard
        </h1>
        <p className="text-text-secondary dark:text-text-secondary-dark">
          Welcome back! Here's what's happening with your fleet today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 xl:gap-8">
        <div className="bg-surface dark:bg-surface-dark rounded-2xl p-4 sm:p-6 border border-border dark:border-border-dark shadow-lg w-full overflow-hidden">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-text dark:text-text-dark">Revenue Overview</h2>
              <p className="text-xs sm:text-sm text-text-secondary dark:text-text-secondary-dark">Performance metrics</p>
            </div>
          </div>
          {revenueData.length > 0 ? (
            <div className="w-full" style={{ height: '300px', minHeight: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00BCD4" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00BCD4" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" opacity={0.3} />
                  <XAxis 
                    dataKey="name" 
                    stroke="#757575" 
                    fontSize={12}
                    tick={{ fill: '#757575' }}
                  />
                  <YAxis 
                    stroke="#757575" 
                    fontSize={12}
                    tick={{ fill: '#757575' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }} 
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#00BCD4"
                    strokeWidth={2}
                    fill="url(#colorRevenue)"
                    name="Revenue (NOK)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="w-full flex items-center justify-center" style={{ height: '300px', minHeight: '250px' }}>
              <div className="flex flex-col items-center justify-center text-center px-6 py-8">
                <div className="w-16 h-16 rounded-full bg-primary/10 dark:bg-primary-dark/10 flex items-center justify-center mb-4">
                  <DollarSign className="w-8 h-8 text-primary dark:text-primary-dark opacity-50" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-text dark:text-text-dark mb-2">
                  No Revenue Data
                </h3>
                <p className="text-sm text-text-secondary dark:text-text-secondary-dark max-w-xs leading-relaxed">
                  Revenue data will appear here once rides are completed
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-surface dark:bg-surface-dark rounded-2xl p-4 sm:p-6 border border-border dark:border-border-dark shadow-lg w-full overflow-hidden">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-text dark:text-text-dark">Rides Trend</h2>
              <p className="text-xs sm:text-sm text-text-secondary dark:text-text-secondary-dark">Activity over time</p>
            </div>
          </div>
          {ridesData.length > 0 ? (
            <div className="w-full" style={{ height: '300px', minHeight: '250px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ridesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" opacity={0.3} />
                  <XAxis 
                    dataKey="name" 
                    stroke="#757575" 
                    fontSize={12}
                    tick={{ fill: '#757575' }}
                  />
                  <YAxis 
                    stroke="#757575" 
                    fontSize={12}
                    tick={{ fill: '#757575' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }} 
                  />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="rides"
                    stroke="#00BCD4"
                    strokeWidth={3}
                    dot={{ fill: '#00BCD4', r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Rides"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="w-full flex items-center justify-center" style={{ height: '300px', minHeight: '250px' }}>
              <div className="flex flex-col items-center justify-center text-center px-6 py-8">
                <div className="w-16 h-16 rounded-full bg-primary/10 dark:bg-primary-dark/10 flex items-center justify-center mb-4">
                  <TrendingUp className="w-8 h-8 text-primary dark:text-primary-dark opacity-50" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-text dark:text-text-dark mb-2">
                  No Rides Data
                </h3>
                <p className="text-sm text-text-secondary dark:text-text-secondary-dark max-w-xs leading-relaxed">
                  Ride trends will appear here once rides are completed
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="bg-gradient-to-br from-success/10 to-success/5 dark:from-success-dark/10 dark:to-success-dark/5 rounded-2xl p-6 border border-success/20 dark:border-success-dark/20 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-text-secondary dark:text-text-secondary-dark">Week Revenue</h3>
            <DollarSign className="w-5 h-5 text-success" />
          </div>
          <p className="text-3xl font-bold text-text dark:text-text-dark">
            NOK {(kpis.weekRevenue ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-gradient-to-br from-warning/10 to-warning/5 dark:from-warning-dark/10 dark:to-warning-dark/5 rounded-2xl p-6 border border-warning/20 dark:border-warning-dark/20 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-text-secondary dark:text-text-secondary-dark">Month Revenue</h3>
            <TrendingUp className="w-5 h-5 text-warning" />
          </div>
          <p className="text-3xl font-bold text-text dark:text-text-dark">
            NOK {(kpis.monthRevenue ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-gradient-to-br from-info/10 to-info/5 dark:from-info-dark/10 dark:to-info-dark/5 rounded-2xl p-6 border border-info/20 dark:border-info-dark/20 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-text-secondary dark:text-text-secondary-dark">Avg Driver Rating</h3>
            <UserCheck className="w-5 h-5 text-info" />
          </div>
          <p className="text-3xl font-bold text-text dark:text-text-dark">
            {(kpis.averageDriverRating ?? 0).toFixed(1)} ⭐
          </p>
        </div>
      </div>
    </div>
  );
}
